import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { getDefaultRoute } from '@/lib/roles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Lock, Mail } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result?.success && result?.user) {
        toast.success(`Welcome back, ${result?.user?.name ?? 'User'}!`);
        navigate(getDefaultRoute(result.user.role));
      } else {
        toast.error(result?.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      toast.error('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#0f172a' }}>
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://images.unsplash.com/photo-1563457039413-0c39b8e8963d?auto=format&fit=crop&q=80"
            alt="Office"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 flex flex-col justify-center p-12">
          <h1 className="text-5xl font-bold tracking-tight text-white font-[Manrope] mb-4">
            <span>ABD</span><span style={{ color: '#0047AB' }}> HR</span>
          </h1>
          <p className="text-lg text-white/70 max-w-md leading-relaxed">
            Building Dreams on Trust.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-6 xl:grid-cols-4">
            {[
              { value: '8+', label: 'Completed Projects' },
              { value: '1000+', label: 'Luxury Villas' },
              { value: '500+', label: 'Premium Apartments' },
              { value: '20+', label: 'Years of Experience' },
            ].map(stat => (
              <div key={stat.label}>
                <p className="text-2xl font-bold text-white font-[JetBrains Mono]">{stat.value}</p>
                <p className="text-xs text-white/50 uppercase tracking-widest mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <h1 className="text-3xl font-bold tracking-tight font-[Manrope]">
              <span>ABD</span><span style={{ color: '#0047AB' }}> HR</span>
            </h1>
          </div>

          <Card className="border border-border shadow-sm">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold tracking-tight font-[Manrope]">Sign In</CardTitle>
              <CardDescription>Enter your credentials to access the system</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="admin@nexhr.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-widest font-medium text-muted-foreground">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type="password"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <Button
                  data-testid="login-submit-btn"
                  type="submit"
                  className="w-full bg-[#0047AB] hover:bg-[#003d96] text-white"
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
